import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Typography, Skeleton, alpha } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useTheme } from '@mui/material/styles';
import { useLocale } from '../../locale/LocaleProvider';
import { Link as RouterLink } from 'react-router-dom';
import { getIconByName } from '../../utils/iconMap';
import { isInternalAppPath } from '../../utils/internalPath';
import { resolvePublicUrl } from '../../utils/publicUrl';
import { portalContentMaxWidthSx, portalPageHorizontalPaddingSx } from '../../theme/portalLayout';
/**
 * Full-bleed band below the hero: large permit-browse tiles (data from the same source as the rest of the app).
 */
export function LandingBrowseBand({ tiles, loading, loadError, heading, subheading, bandMarginBottom, trustFeaturesPresent, }) {
    const theme = useTheme();
    const ll = theme.landingHeroLayout;
    const { shell } = useLocale();
    if (!(tiles.length > 0 || loading || loadError)) {
        return null;
    }
    return (_jsx(Box, { sx: {
            width: '100%',
            bgcolor: '#ffffff',
            ...portalPageHorizontalPaddingSx,
            /* When trust features sit above, use a smaller top padding to reduce gap */
            pt: trustFeaturesPresent ? { xs: '0.5rem', md: '0.75rem' } : 0,
            pb: { xs: 1.75, md: 2 },
            position: 'relative',
            zIndex: 1,
        }, children: _jsx(Box, { sx: { ...portalContentMaxWidthSx }, children: _jsxs(Box, { id: "departments", sx: {
                    mt: trustFeaturesPresent
                        ? ll.departmentsBand.headingBlock.marginTopWithTrust
                        : ll.departmentsBand.headingBlock.marginTopNoTrust,
                    mb: bandMarginBottom,
                    position: 'relative',
                    scrollMarginTop: 16,
                }, children: [_jsxs(Box, { sx: {
                            px: { xs: 1.5, sm: 2, md: 3 },
                            pt: { xs: 0.5, md: 0.5 },
                            pb: { xs: 0.5, md: 0.75 },
                        }, children: [_jsx(Typography, { variant: "h3", component: "h2", sx: {
                                    m: 0,
                                    mb: 1,
                                    fontWeight: 800,
                                    color: 'primary.dark',
                                    fontSize: { xs: '1.4rem', md: '1.75rem' },
                                    textAlign: 'center',
                                    width: '100%',
                                }, children: heading }), subheading ? (_jsx(Typography, { variant: "body1", sx: {
                                    m: 0,
                                    mb: { xs: 2, md: 2.5 },
                                    mx: 'auto',
                                    maxWidth: 720,
                                    color: 'text.secondary',
                                    lineHeight: 1.5,
                                    fontSize: { xs: '0.9rem', md: '0.98rem' },
                                    textAlign: 'center',
                                }, children: subheading })) : null] }), _jsxs(Box, { sx: {
                            borderRadius: { xs: 2.5, md: 3 },
                            overflow: 'hidden',
                            /* Use fully opaque white here so nothing from the hero shows through */
                            bgcolor: theme.palette.common.white,
                            border: 'none',
                            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.dark, 0.06)}`,
                        }, children: [loading && tiles.length === 0 && !loadError ? (_jsx(Box, { component: "ul", "aria-busy": "true", "aria-label": `${heading} loading`, sx: {
                                    listStyle: 'none',
                                    m: 0,
                                    p: 0,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    gap: 0,
                                    alignItems: 'stretch',
                                }, children: Array.from({ length: 4 }).map((_, i) => (_jsx(Box, { component: "li", sx: {
                                        flex: { xs: 'none', md: '1 1 0' },
                                        minWidth: 0,
                                        width: { xs: '100%', md: 'auto' },
                                    }, children: _jsx(Skeleton, { variant: "rounded", animation: "wave", sx: {
                                            width: '100%',
                                            minHeight: ll.departmentsBand.tileMinHeights,
                                            borderRadius: 0,
                                            bgcolor: alpha(theme.palette.common.white, 0.65),
                                        } }) }, i))) })) : null, loadError ? (_jsx(Typography, { color: "error", variant: "body2", sx: { textAlign: 'center', py: 3, px: 2 }, children: shell.heroDepartmentsLoadError })) : null, !loading && !loadError && tiles.length > 0 ? (_jsx(Box, { component: "ul", role: "list", sx: {
                                    listStyle: 'none',
                                    m: 0,
                                    p: 0,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    alignItems: 'stretch',
                                    gap: 0,
                                }, children: tiles.map((dept, i) => {
                                    const rawTileImg = dept.image ? (resolvePublicUrl(dept.image) ?? dept.image) : '';
                                    const imageSrc = rawTileImg && !/^data:/i.test(rawTileImg) ? rawTileImg : '';
                                    const rawLogo = dept.logoImage ? (resolvePublicUrl(dept.logoImage) ?? dept.logoImage) : '';
                                    const logoSrc = rawLogo && !/^data:/i.test(rawLogo) ? rawLogo : '';
                                    const TileIcon = dept.icon ? getIconByName(dept.icon) : null;
                                    const isLast = i === tiles.length - 1;
                                    const showHeroPhoto = Boolean(imageSrc);
                                    const badgeIcon = logoSrc && !showHeroPhoto ? (_jsx(Box, { component: "img", loading: "lazy", decoding: "async", src: logoSrc, alt: "", sx: {
                                            width: ll.departmentsBand.tileBadgeWidthPct,
                                            height: ll.departmentsBand.tileBadgeWidthPct,
                                            objectFit: 'contain',
                                            filter: 'brightness(0) invert(1)',
                                        } })) : TileIcon ? (_jsx(TileIcon, { sx: {
                                            fontSize: ll.departmentsBand.tileIconSizes,
                                            color: theme.palette.common.white,
                                        } })) : (_jsx(AccountBalanceIcon, { sx: {
                                            fontSize: ll.departmentsBand.tileIconSizes,
                                            color: theme.palette.common.white,
                                        }, "aria-hidden": true }));
                                    const tileContent = (_jsxs(_Fragment, { children: [showHeroPhoto ? (_jsx(Box, { className: "landing-dept-tile-bg", "aria-hidden": true, sx: {
                                                    position: 'absolute',
                                                    inset: 0,
                                                    backgroundImage: `url(${imageSrc})`,
                                                    backgroundSize: dept.imageFit === 'contain' ? 'contain' : 'cover',
                                                    backgroundPosition: 'center center',
                                                    backgroundRepeat: dept.imageFit === 'contain' ? 'no-repeat' : undefined,
                                                    bgcolor: dept.imageFit === 'contain' ? theme.palette.primary.dark : undefined,
                                                    transition: 'transform 0.4s ease',
                                                } })) : (_jsx(Box, { "aria-hidden": true, sx: {
                                                    position: 'absolute',
                                                    inset: 0,
                                                    bgcolor: theme.palette.primary.dark,
                                                } })), _jsx(Box, { "aria-hidden": true, sx: {
                                                    position: 'absolute',
                                                    inset: 0,
                                                    pointerEvents: 'none',
                                                    background: showHeroPhoto
                                                        ? `linear-gradient(to top, ${alpha(theme.palette.primary.dark, 0.94)} 0%, ${alpha(theme.palette.primary.dark, 0.42)} 42%, ${alpha(theme.palette.common.black, 0.05)} 100%)`
                                                        : `linear-gradient(to top, ${alpha(theme.palette.primary.dark, 0.88)} 0%, transparent 55%)`,
                                                } }), _jsxs(Box, { sx: {
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    flex: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-end',
                                                    minHeight: ll.departmentsBand.tileMinHeights,
                                                    px: { xs: 1.5, md: 1.75 },
                                                    pt: { xs: 5, md: 5.5 },
                                                    pb: { xs: 2, md: 2.25 },
                                                }, children: [_jsx(Box, { sx: {
                                                            width: ll.departmentsBand.tileBadgeBox.width,
                                                            height: ll.departmentsBand.tileBadgeBox.height,
                                                            borderRadius: 2,
                                                            mx: 'auto',
                                                            mb: 1.25,
                                                            flexShrink: 0,
                                                            bgcolor: 'primary.main',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.dark, 0.45)}`,
                                                        }, children: badgeIcon }), _jsx(Typography, { component: "span", variant: "subtitle2", sx: {
                                                            textAlign: 'center',
                                                            fontWeight: 700,
                                                            lineHeight: 1.35,
                                                            color: theme.palette.common.white,
                                                            letterSpacing: '-0.01em',
                                                            fontSize: ll.departmentsBand.tileTitleFontSize,
                                                            maxWidth: '100%',
                                                            mx: 'auto',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textShadow: `0 1px 12px ${alpha(theme.palette.primary.dark, 0.85)}`,
                                                        }, children: dept.title })] })] }));
                                    const tileSx = {
                                        flex: { xs: '1 1 auto', md: '1 1 0' },
                                        width: '100%',
                                        minWidth: 0,
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                        textAlign: 'center',
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        borderBottom: {
                                            xs: isLast ? 'none' : `1px solid ${alpha(theme.palette.primary.dark, 0.12)}`,
                                            md: 'none',
                                        },
                                        borderRight: {
                                            xs: 'none',
                                            md: isLast ? 'none' : `1px solid ${alpha(theme.palette.primary.dark, 0.12)}`,
                                        },
                                        transition: 'box-shadow 0.2s ease',
                                        '&:hover': {
                                            boxShadow: `inset 0 0 0 999px ${alpha(theme.palette.common.black, 0.06)}`,
                                            '& .landing-dept-tile-bg': {
                                                transform: 'scale(1.05)',
                                            },
                                        },
                                        '&:focus-visible': {
                                            outline: `2px solid ${theme.palette.secondary.main}`,
                                            outlineOffset: -2,
                                        },
                                    };
                                    return (_jsx(Box, { component: "li", sx: { flex: { md: '1 1 0' }, width: { xs: '100%', md: 'auto' }, minWidth: 0, display: 'flex' }, children: isInternalAppPath(dept.href) ? (_jsx(Box, { component: RouterLink, to: dept.href, sx: tileSx, children: tileContent })) : (_jsx(Box, { component: "a", href: dept.href || '#', sx: tileSx, children: tileContent })) }, dept.recordId ?? dept.href ?? i));
                                }) })) : null] })] }) }) }));
}
